"use client";

import { useState } from "react";
import moment from "moment";
import { X, Phone, Video, MessageSquare, User } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogHeader } from "../ui/dialog";
import { DialogTitle } from "@radix-ui/react-dialog";
import { button } from "../ui/button";
import { cn } from "../lib/utill";
import Audio from "../../assets/images/svg/Audio";
import {
  AudioIcon,
  ChatIcon,
  InPersonIcon,
  VideoIcon,
} from "../../assets/images/svg";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Checkbox, TimePicker } from "antd";
import dayjs from "dayjs";

export default function UpdateSlotDetailsModal({
  slot,
  onSave,
  onClose,
  open,
  selectedDay,
  sameForAllWeek,
  setEditDialog,
  editSelectedDuration,
  editSelectedModes,
  updateSlot,
  setSameForAllWeek,
}) {
  const [duration, setDuration] = useState(editSelectedDuration);
  const [consultationModes, setConsultationModes] = useState(editSelectedModes);
  const [applyToDays, setApplyToDays] = useState(selectedDay);
  const dayOfWeek = moment(slot.start).format("dddd");
  const startTime = moment(slot.start).format("h:mm A");
  const endTime = moment(slot.end).format("h:mm A");

  const handleDurationSelect = (mins) => {
    setDuration(mins);
  };

  const handleModeToggle = (mode) => {
    if (consultationModes.includes(mode)) {
      setConsultationModes(consultationModes.filter((m) => m !== mode));
    } else {
      setConsultationModes([...consultationModes, mode]);
    }
  };

  const handleDayToggle = (day) => {
    if (applyToDays.includes(day)) {
      setApplyToDays(applyToDays.filter((d) => d !== day));
    } else {
      setApplyToDays([...applyToDays, day]);
    }
  };

  const handleSave = () => {
    onSave({
      duration,
      consultationModes,
    });
  };

  const days = [
    { name: "Mon", value: "MONDAY" },
    { name: "Tue", value: "TUESDAY" },
    { name: "Wed", value: "WEDNESDAY" },
    { name: "Thu", value: "THURSDAY" },
    { name: "Fri", value: "FRIDAY" },
    { name: "Sat", value: "SATURDAY" },
    { name: "Sun", value: "SUNDAY" },
  ];

  const durations = [
    {
      label: "15 min",
      value: 15,
      bgColor: "bg-[#BDFFE1]",
      textColor: "text-green-800",
      borderColor: "border-green-200",
    },
    {
      label: "30 min",
      value: 30,
      bgColor: "bg-[#BDDBFF]",
      textColor: "text-blue-800",
      borderColor: "border-blue-200",
    },
    {
      label: "45 min",
      value: 45,
      bgColor: "bg-[#FFBDDB]",
      textColor: "text-pink-800",
      borderColor: "border-pink-200",
    },
    {
      label: "60 min",
      value: 60,
      bgColor: "bg-[#FFE1BD]",
      textColor: "text-orange-800",
      borderColor: "border-orange-200",
    },
  ];

  function updateTime(isoString, newTime) {
    if (!isoString || typeof isoString !== "string") {
      console.error("Invalid ISO string passed:", isoString);
      return null;
    }

    let datePart = isoString.substring(0, 11); // "YYYY-MM-DDT"
    return `${datePart}${newTime}:00.000Z`; // "YYYY-MM-DDTHH:mm:00.000Z"
  }

  return (
    <div open={open} onOpenChange={(open) => !open && onClose()}>
      <div className="sm:max-w-md">
        <div>
          <div className="flex items-center justify-between">
            <div className="text-[#0B4F9F] text-[14px] font-semibold">
              Slot Details
            </div>

            <div asChild>
              <button
                variant="ghost"
                size="icon"
                className="h-6 w-6 bg-transparent "
                onClick={() => onClose()}
              >
                <X className="h-4 w-4 text-[#334155]" />
              </button>
            </div>
          </div>
          <div>
            <Checkbox
              checked={sameForAllWeek}
              onChange={(e) => setSameForAllWeek(!sameForAllWeek)}
              className="text-[#171717] text-[14px] font-normal font-poppins"
            >
              Copy the same for all weeks
            </Checkbox>
          </div>
        </div>

        <div className="space-y-4 mt-4">
          <div>
            <h3 className="text-[#171717] text-[14px] font-poppins font-semibold">
              {dayOfWeek}
            </h3>
            <div className="flex gap-2">
              <TimePicker
                placeholder={"Start Time"}
                defaultValue={dayjs(startTime, "HH:mm A")}
                format={"HH:mm A"}
                use12Hours={false}
                onChange={(v, string) => {
                  slot.start = v;
                }}
              />
              <TimePicker
                placeholder={"End Time"}
                defaultValue={dayjs(endTime, "HH:mm A")}
                format={"HH:mm A"}
                use12Hours={false}
                onChange={(v, string) => {
                  slot.end = v;
                }}
              />
            </div>
            {/* <p cl assName="text-[14px] text-[#171717] font-poppins font-normal">
              {startTime} to {endTime}
            </p> */}
          </div>
          <div className="border-t-1 border-[#E2E8F0] my-0.5 mt-2 mb-0" />

          <div>
            <h4 className="mb-4 text-[#171717] text-[14px] font-poppins font-normal ">
              Choose Duration for Each Consultation Slot
            </h4>
            <div className="flex flex-wrap gap-2">
              {durations.map((item) => (
                <button
                  key={item.value}
                  variant="outline"
                  size="sm"
                  className={cn(
                    duration === item.value
                      ? `${item.bgColor} ${item.textColor} ${item.borderColor} text-[16px] font-normal font-poppins`
                      : "bg-[#EFF6FF] text-[#334155] text-[16px] font-normal font-poppins"
                  )}
                  onClick={() => handleDurationSelect(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t-1 border-[#E2E8F0] my-0.5 mt-4 mb-0" />
          <div>
            <h4 className="text-[#171717] text-[14px] font-poppins font-normal mb-4">
              Choose How Patients Can Reach You
            </h4>
            <div className="flex flex-wrap gap-2">
              <button
                variant={
                  consultationModes.includes("audio") ? "default" : "outline"
                }
                size="sm"
                className={cn(
                  "h-8",
                  consultationModes.includes("audio")
                    ? "bg-[#0B4F9F] text-[#6EE7B7] text-[14px] font-poppins flex justify-center items-center"
                    : "flex justify-center items-center bg-[#EFF6FF] text-[#334155] font-poppins text-[14px]"
                )}
                onClick={() => handleModeToggle("audio")}
              >
                <AudioIcon
                  fill={
                    consultationModes.includes("audio") ? "#FFFFFF" : "#334155"
                  }
                />{" "}
                <span className={"ml-1"}>Audio</span>
              </button>
              <button
                variant={
                  consultationModes.includes("video") ? "default" : "outline"
                }
                size="sm"
                className={cn(
                  "h-8",
                  consultationModes.includes("video")
                    ? "bg-[#0B4F9F] text-[#6EE7B7] text-[14px] font-poppins flex justify-center items-center"
                    : "flex justify-center items-center bg-[#EFF6FF] text-[#334155] font-poppins text-[14px]"
                )}
                onClick={() => handleModeToggle("video")}
              >
                <VideoIcon
                  fill={
                    consultationModes.includes("video") ? "#FFFFFF" : "#334155"
                  }
                />{" "}
                <span className="ml-1">Video</span>
              </button>
              <button
                variant={
                  consultationModes.includes("chat") ? "default" : "outline"
                }
                size="sm"
                className={cn(
                  "h-8",
                  consultationModes.includes("chat")
                    ? "bg-[#0B4F9F] text-[#6EE7B7] text-[14px] font-poppins flex justify-center items-center"
                    : "flex justify-center items-center bg-[#EFF6FF] text-[#334155] font-poppins text-[14px]"
                )}
                onClick={() => handleModeToggle("chat")}
              >
                <ChatIcon
                  fill={
                    consultationModes.includes("chat") ? "#FFFFFF" : "#334155"
                  }
                />
                <span className="ml-1">Chat</span>
              </button>
              <button
                variant={
                  consultationModes.includes("in-person")
                    ? "default"
                    : "outline"
                }
                size="sm"
                className={cn(
                  "h-8",
                  consultationModes.includes("in-person")
                    ? "bg-[#0B4F9F] text-[#6EE7B7] text-[14px] font-poppins flex justify-center items-center"
                    : "flex justify-center items-center bg-[#EFF6FF] text-[#334155] font-poppins text-[14px]"
                )}
                onClick={() => handleModeToggle("in-person")}
              >
                <InPersonIcon
                  fill={
                    consultationModes.includes("in-person")
                      ? "#FFFFFF"
                      : "#334155"
                  }
                />{" "}
                <span className="ml-1">In-person</span>
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              className="btn-gradient rounded-xl h-8 flex justify-center items-center text-[#FFFFFF] text-[14px] font-normal font-poppins"
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
